/**
 * @module Document model for the Tiptap rich text composer.
 *
 * Ported from t3code's composer rich-text layer (ticket 3526, upstream pin
 * e3e7cc3f, MIT license retained in LICENSE), adapted to Forge's prompt token
 * vocabulary: `@path` mentions, `$name` skills, assistant citation chips, and
 * terminal-context placeholder chips (`INLINE_TERMINAL_CONTEXT_PLACEHOLDER`).
 *
 * The stored prompt stays plain text plus those tokens (`**bold**` when rich
 * text is on). The Tiptap document holds styled text plus inline atom chips,
 * so this module translates prompt text into ProseMirror JSON the editor can
 * mount. The serializer half lives in `composer-rich-text-serialize.ts`.
 *
 * DOM-free on purpose: unit tests build a real ProseMirror document from the
 * JSON this produces and assert the round trip without a browser.
 */

import { Code } from "@tiptap/extension-code";
import { TaskItem } from "@tiptap/extension-task-item";
import { wrappingInputRule } from "@tiptap/core";

import {
  splitPromptIntoComposerSegments,
  type ComposerPromptSegment,
} from "./composer-editor-mentions";
import { parseInlineMarkdown, type RichTextMark } from "./composer-rich-text";
import {
  INLINE_TERMINAL_CONTEXT_PLACEHOLDER,
  type TerminalContextDraft,
} from "./lib/terminalContext";

export type SkillMeta = { label: string; description: string | null };

/** Outermost mark first, so closers mirror openers when nested. */
export const MARK_NESTING_ORDER: RichTextMark[] = ["strike", "bold", "italic", "code"];

const MARK_TO_TIPTAP: Record<RichTextMark, string> = {
  bold: "bold",
  italic: "italic",
  strike: "strike",
  code: "code",
};

/**
 * Tiptap's code mark excludes every other mark, which rejects the `bold+code`
 * spans markdown like `**\`x\`**` parses into and drops the whole insert.
 * Code nests inside emphasis here, so it only excludes itself like the rest.
 */
export const ComposerCodeExtension = Code.extend({ excludes: "code" });

/**
 * Task list items keep their exact source indent in an attribute so nesting
 * round-trips byte-identically. Checkbox case (`[X]`) normalizes to `[x]`, the
 * same fixed-point deal as `__bold__` becoming `**bold**`.
 */
export const ComposerTaskItemExtension = TaskItem.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      indent: { default: "" },
      markerSpace: { default: " " },
      contentSpace: { default: null },
    };
  },
  addInputRules() {
    return [
      ...(this.parent?.() ?? []),
      wrappingInputRule({
        find: /^- \[([ xX])\] $/,
        type: this.type,
        getAttributes: (match) => ({ checked: match[1]?.toLowerCase() === "x" }),
      }),
    ];
  },
}).configure({ nested: true });

function randomNodeKey(): string {
  return `tiptap-${Math.random().toString(36).slice(2)}`;
}

interface TaskLinePrefix {
  indent: string;
  checked: boolean;
  markerSpace: string;
  contentSpace: string;
}

function parseTaskPrefix(head: string): { prefix: TaskLinePrefix; markerLength: number } | null {
  const match = head.match(/^([ \t]*)-([ \t]+)\[([ xX])\]([ \t]*)/);
  if (!match) return null;
  const after = head.slice(match[0].length);
  if (after.length > 0 && !match[4]) return null;
  return {
    prefix: {
      indent: match[1] ?? "",
      checked: (match[3] ?? " ").toLowerCase() === "x",
      markerSpace: match[2]!,
      contentSpace: match[4]!,
    },
    markerLength: match[0].length,
  };
}

type InlineJson = Record<string, unknown>;

interface DocLine {
  task: TaskLinePrefix | null;
  inline: InlineJson[];
}

function atomJsonForSegment(
  segment: Exclude<ComposerPromptSegment, { type: "text" }>,
  skillLabelFor: (name: string) => SkillMeta,
): InlineJson {
  if (segment.type === "mention") {
    return {
      type: "composer-mention",
      attrs: { path: segment.path, source: `@${segment.path}` },
    };
  }
  if (segment.type === "skill") {
    const meta = skillLabelFor(segment.name);
    return {
      type: "composer-skill",
      attrs: {
        skillName: segment.name,
        skillLabel: meta.label,
        skillDescription: meta.description,
      },
    };
  }
  if (segment.type === "citation") {
    return {
      type: "composer-citation",
      attrs: { citation: segment.citation, source: segment.source, citeKey: randomNodeKey() },
    };
  }
  return {
    type: "composer-terminal-context",
    attrs: { context: segment.context, source: INLINE_TERMINAL_CONTEXT_PLACEHOLDER },
  };
}

/**
 * A terminal-context placeholder with no matching draft renders nothing (the
 * Lexical composer dropped it the same way), so it folds back into text here
 * before atoms are assigned sentinels.
 */
function resolvedPromptSegments(
  value: string,
  terminalContexts: ReadonlyArray<TerminalContextDraft>,
): ComposerPromptSegment[] {
  return splitPromptIntoComposerSegments(value, terminalContexts).map((segment) =>
    segment.type === "terminal-context" && !segment.context ? { type: "text", text: "" } : segment,
  );
}

interface PendingTaskItem extends TaskLinePrefix {
  content: InlineJson[];
  children: PendingTaskItem[];
}

function taskListJson(items: PendingTaskItem[]): InlineJson {
  return {
    type: "taskList",
    content: items.map((item) => ({
      type: "taskItem",
      attrs: {
        checked: item.checked,
        indent: item.indent,
        markerSpace: item.markerSpace,
        contentSpace: item.contentSpace,
      },
      content: [
        { type: "paragraph", content: item.content },
        ...(item.children.length > 0 ? [taskListJson(item.children)] : []),
      ],
    })),
  };
}

function textJsonForSpan(text: string, marks: RichTextMark[]): Record<string, unknown> {
  const json: Record<string, unknown> = { type: "text", text };
  if (marks.length > 0) {
    json.marks = marks
      .toSorted((a, b) => MARK_NESTING_ORDER.indexOf(a) - MARK_NESTING_ORDER.indexOf(b))
      .map((mark) => ({ type: MARK_TO_TIPTAP[mark] }));
  }
  return json;
}

/** Nest a task line under the open indent stack, sibling to equal indents. */
function placeTaskItem(
  blocks: Record<string, unknown>[],
  stack: { indent: string; items: PendingTaskItem[] }[],
  item: PendingTaskItem,
): void {
  for (;;) {
    const top = stack[stack.length - 1];
    if (!top) {
      // A leading indented item with no parent flattens but keeps indent.
      stack.push({ indent: item.indent, items: [] });
      continue;
    }
    if (top.indent === item.indent) {
      top.items.push(item);
      return;
    }
    if (top.indent !== "" && !item.indent.startsWith(top.indent)) {
      if (stack.length > 1) {
        stack.pop();
        continue;
      }
      top.indent = item.indent;
      top.items.push(item);
      return;
    }
    const parent = top.items[top.items.length - 1];
    if (!parent) {
      top.items.push(item);
      return;
    }
    parent.children.push(item);
    stack.push({ indent: item.indent, items: parent.children });
    return;
  }
}

function buildTiptapContent(
  value: string,
  skillLabelFor: (name: string) => SkillMeta,
  options?: { styling?: boolean; terminalContexts?: ReadonlyArray<TerminalContextDraft> },
): Record<string, unknown>[] {
  const styling = options?.styling ?? true;
  const segments = resolvedPromptSegments(value, options?.terminalContexts ?? []);
  // Hide token source from the markdown parser, then restore the atoms with
  // the marks of their surrounding text. Pick a sentinel the input cannot
  // contain: the private-use area, since the terminal-context placeholder is a
  // real character in stored prompts.
  let sentinel = String.fromCodePoint(0xe000);
  for (
    let codePoint = 0xe000;
    segments.some((segment) => segmentText(segment).includes(sentinel));
    codePoint += 1
  ) {
    sentinel = String.fromCodePoint(codePoint);
  }
  const atoms: InlineJson[] = [];
  const text = segments
    .map((segment) => {
      if (segment.type === "text") return segment.text;
      atoms.push(atomJsonForSegment(segment, skillLabelFor));
      return sentinel;
    })
    .join("");
  let atomIndex = 0;
  const lines: DocLine[] = text.split("\n").map((line) => {
    const parsed = styling ? parseTaskPrefix(line) : null;
    const content = parsed ? line.slice(parsed.markerLength) : line;
    const spans = styling
      ? parseInlineMarkdown(content)
      : [{ text: content, marks: [] as RichTextMark[] }];
    const inline: InlineJson[] = [];
    for (const span of spans) {
      span.text.split(sentinel).forEach((piece, index) => {
        if (index > 0) {
          const atom = atoms[atomIndex++]!;
          inline.push({ ...atom, marks: textJsonForSpan("", span.marks).marks });
        }
        if (piece) inline.push(textJsonForSpan(piece, span.marks));
      });
    }
    return { task: parsed?.prefix ?? null, inline };
  });

  // Pass 2: consecutive task lines group into (possibly nested) task lists by
  // indent prefix; everything else stays a paragraph.
  const blocks: Record<string, unknown>[] = [];
  let stack: { indent: string; items: PendingTaskItem[] }[] = [];
  const flushTasks = () => {
    if (stack.length > 0) {
      blocks.push(taskListJson(stack[0]!.items));
      stack = [];
    }
  };
  for (const line of lines) {
    if (!line.task) {
      flushTasks();
      blocks.push({ type: "paragraph", content: line.inline });
      continue;
    }
    const item: PendingTaskItem = {
      ...line.task,
      content: line.inline,
      children: [],
    };
    placeTaskItem(blocks, stack, item);
  }
  flushTasks();
  return blocks;
}

function segmentText(segment: ComposerPromptSegment): string {
  return segment.type === "text" ? segment.text : segmentSourceText(segment);
}

/** The characters a segment contributes to the stored prompt. */
function segmentSourceText(segment: Exclude<ComposerPromptSegment, { type: "text" }>): string {
  if (segment.type === "mention") return `@${segment.path}`;
  if (segment.type === "skill") return `$${segment.name}`;
  if (segment.type === "terminal-context") return INLINE_TERMINAL_CONTEXT_PLACEHOLDER;
  return segment.source;
}

export function buildDocJson(
  value: string,
  skillLabelFor: (name: string) => SkillMeta,
  options?: { styling?: boolean; terminalContexts?: ReadonlyArray<TerminalContextDraft> },
) {
  return { type: "doc", content: buildTiptapContent(value, skillLabelFor, options) };
}

/**
 * Insert pasted prompt text at the selection, rebuilding inline tokens as
 * chips and styled spans as marks.
 *
 * Newlines always become paragraph splits, never trailing hard breaks, which
 * render no visible line, so pasted text lands exactly as typed.
 */
export function insertMarkdownParagraphs(
  value: string,
  skillLabelFor: (name: string) => SkillMeta,
  options: { styling: boolean; terminalContexts?: ReadonlyArray<TerminalContextDraft> },
  insertContent: (content: Record<string, unknown> | Record<string, unknown>[]) => void,
): void {
  const blocks = buildTiptapContent(value, skillLabelFor, options);
  if (blocks.length === 1 && blocks[0]?.type === "paragraph") {
    const inline = (blocks[0]?.content ?? []) as Record<string, unknown>[];
    if (inline.length === 0) return;
    insertContent(inline);
    return;
  }
  insertContent(blocks);
}
